import { useCallback, useState } from 'react'
import { useFileValidation } from 'hooks/useFileValidation'
import { useToast } from '@chakra-ui/core'

interface UseDropzoneHandlersProps {
  onUploadMedia: (files: File[]) => Promise<void>
  accept?: string
}

export const useDropzoneHandlers = ({ onUploadMedia, accept }: UseDropzoneHandlersProps) => {
  const { fileSizeValidation } = useFileValidation()
  const toast = useToast()
  const [droppedMedia, setDroppedMedia] = useState<File | null>(null)

  const validateFileType = useCallback(
    (file: File) => {
      const fileType = file.type
      const fileName = file.name.toLowerCase()

      if (accept?.includes('image/*') && fileType.startsWith('image')) return true
      if (accept?.includes('video/*') && fileType.startsWith('video')) return true

      // Проверяем расширения файлов
      const extensions = accept?.split(',').filter((ext) => ext.startsWith('.'))
      if (extensions?.some((ext) => fileName.endsWith(ext))) return true

      // Если тип файла пустой или 'file', проверяем только по расширению
      if (!fileType || fileType === 'file') {
        return extensions?.some((ext) => fileName.endsWith(ext)) || false
      }

      return false
    },
    [accept]
  )

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return

      const file = acceptedFiles[0]
      const isValid = fileSizeValidation(file)

      if (!isValid) return

      if (validateFileType(file)) {
        setDroppedMedia(file)
        await onUploadMedia(acceptedFiles)
      } else {
        toast({
          title: 'wrong file type',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    },
    [fileSizeValidation, validateFileType, onUploadMedia, toast]
  )

  const handlePaste = useCallback(
    async (event: React.ClipboardEvent) => {
      if (event.clipboardData && event.clipboardData.files && event.clipboardData.files.length > 0) {
        const files = Array.from(event.clipboardData.files)
        const file = files[0]
        const isValid = fileSizeValidation(file)

        if (!isValid) return

        if (validateFileType(file)) {
          setDroppedMedia(file)
          await onUploadMedia(files)
        } else {
          toast({
            title: 'wrong file type',
            status: 'error',
            duration: 3000,
            isClosable: true,
          })
        }
      }
    },
    [fileSizeValidation, validateFileType, onUploadMedia, toast]
  )

  return {
    onDrop,
    handlePaste,
    droppedMedia,
  }
}
